import { useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';
import { setTokenPair, type TokenPair } from '../lib/auth';
import { apiPost } from '../lib/api';
import { AuthLayout } from '../components/layout/AuthLayout';
import { useAuth } from '../contexts/AuthContext';
import { useAuthConfig } from '../hooks/useAuthConfig';
import type { PublicAuthConfig } from '../hooks/useAuthConfig';

// ─── Dynamic schema builder ──────────────────────────────────────────────────

function buildSchema(config: PublicAuthConfig | undefined) {
  const policy = config?.passwordPolicy ?? {
    minLength: 8,
    maxLength: 128,
    requireUppercase: false,
    requireDigit: false,
    requireSpecialChar: false,
  };

  const passwordSchema = z.string()
    .min(policy.minLength, `Password must be at least ${policy.minLength} characters`)
    .max(policy.maxLength, `Password must be at most ${policy.maxLength} characters`)
    .superRefine((val, ctx) => {
      if (policy.requireUppercase && !/[A-Z]/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Password must contain at least one uppercase letter',
        });
      }
      if (policy.requireDigit && !/[0-9]/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Password must contain at least one digit',
        });
      }
      if (policy.requireSpecialChar && !/[^A-Za-z0-9]/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Password must contain at least one special character',
        });
      }
    });

  // Build dynamic field schemas
  const dynamicShape: Record<string, z.ZodTypeAny> = {};
  for (const field of config?.signupFields ?? []) {
    let fieldSchema: z.ZodTypeAny = z.string();
    if (field.type === 'email') {
      fieldSchema = z.string().email('Enter a valid email');
    }
    if (!field.required) {
      fieldSchema = fieldSchema.optional().or(z.literal(''));
    } else {
      fieldSchema = z.string().min(1, `${field.label} is required`);
    }
    dynamicShape[field.field] = fieldSchema;
  }

  // Build consent checkbox schemas
  const consentShape: Record<string, z.ZodTypeAny> = {};
  for (const consent of config?.consentCheckboxes ?? []) {
    if (consent.required) {
      consentShape[`consent_${consent.id}`] = z.literal(true, {
        errorMap: () => ({ message: 'This consent is required' }),
      });
    } else {
      consentShape[`consent_${consent.id}`] = z.boolean().optional();
    }
  }

  return z
    .object({
      email: z.string().email('Enter a valid email address'),
      password: passwordSchema,
      confirmPassword: z.string(),
      ...dynamicShape,
      ...consentShape,
    })
    .refine((d) => d.password === d.confirmPassword, {
      message: 'Passwords do not match',
      path: ['confirmPassword'],
    });
}

// ─── Password strength ────────────────────────────────────────────────────────

type Strength = 'weak' | 'fair' | 'strong';

function passwordStrength(pw: string, policy: PublicAuthConfig['passwordPolicy'] | undefined): Strength {
  const minLen = policy?.minLength ?? 8;
  if (pw.length < minLen) return 'weak';
  let score = 0;
  if (pw.length >= minLen + 4) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return 'weak';
  if (score <= 2) return 'fair';
  return 'strong';
}

const strengthConfig = {
  weak:   { label: 'Weak',   color: 'bg-red-400',     width: 'w-1/3' },
  fair:   { label: 'Fair',   color: 'bg-amber-400',   width: 'w-2/3' },
  strong: { label: 'Strong', color: 'bg-emerald-500', width: 'w-full' },
};

function PasswordStrengthBar({ password, policy }: { password: string; policy?: PublicAuthConfig['passwordPolicy'] }) {
  if (!password) return null;
  const s = passwordStrength(password, policy);
  const { label, color, width } = strengthConfig[s];
  return (
    <div className="mt-1.5">
      <div className="h-1 w-full bg-zinc-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-300 ${color} ${width}`} />
      </div>
      <p className={`text-xs mt-1 ${s === 'weak' ? 'text-red-500' : s === 'fair' ? 'text-amber-500' : 'text-emerald-600'}`}>
        {label} password
      </p>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SignupPage() {
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const { data: config, isLoading: configLoading } = useAuthConfig();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [apiError, setApiError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);

  const schema = useMemo(() => buildSchema(config), [config]);

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<Record<string, unknown>>({
    resolver: zodResolver(schema),
  });

  const passwordValue = (watch('password') as string) ?? '';

  // If signup disabled, redirect
  if (!configLoading && config && !config.signup.enabled) {
    return <Navigate to="/login" replace />;
  }

  // Loading skeleton
  if (configLoading) {
    return (
      <AuthLayout title="Create account" subtitle="Get started">
        <div className="space-y-5 animate-pulse">
          <div className="grid grid-cols-2 gap-3">
            <div className="h-10 bg-zinc-100 rounded-lg" />
            <div className="h-10 bg-zinc-100 rounded-lg" />
          </div>
          <div className="h-10 bg-zinc-100 rounded-lg" />
          <div className="h-10 bg-zinc-100 rounded-lg" />
          <div className="h-10 bg-zinc-200 rounded-lg" />
        </div>
      </AuthLayout>
    );
  }

  // If verification required and signup succeeded, show message
  if (verificationSent) {
    return (
      <AuthLayout title="Check your email" subtitle="Almost there!">
        <div className="space-y-5 text-center">
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-6">
            <CheckCircle size={24} className="mx-auto mb-2 text-emerald-600" />
            <p className="text-sm font-medium text-emerald-800">
              We sent a verification link to your email
            </p>
            <p className="mt-1 text-xs text-emerald-600">
              Please check your inbox and click the link to activate your account.
            </p>
          </div>
          <p className="text-center text-sm text-zinc-500">
            Already verified?{' '}
            <Link to="/login" className="font-semibold text-[--color-primary] hover:opacity-80">
              Sign in
            </Link>
          </p>
        </div>
      </AuthLayout>
    );
  }

  const onSubmit = async (data: Record<string, unknown>) => {
    setApiError('');

    // Build payload: extract core fields, dynamic fields, and consents
    const { email, password, confirmPassword: _, ...rest } = data;
    const dynamicFields: Record<string, unknown> = {};
    const consents: Record<string, boolean> = {};

    for (const [key, value] of Object.entries(rest)) {
      if (key.startsWith('consent_')) {
        consents[key.replace('consent_', '')] = !!value;
      } else {
        dynamicFields[key] = value;
      }
    }

    const payload = {
      email,
      password,
      ...dynamicFields,
      ...(Object.keys(consents).length > 0 ? { consents } : {}),
    };

    try {
      const res = await apiPost<TokenPair & { requiresVerification?: boolean }>(
        '/customer/auth/signup',
        payload,
        { skipAuth: true },
      );

      if (res.requiresVerification) {
        setVerificationSent(true);
        return;
      }

      setTokenPair(res);
      refreshAuth();
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      if (status === 409) {
        setApiError('An account with this email already exists.');
      } else {
        setApiError('Something went wrong. Please try again.');
      }
    }
  };

  const handleSocialLogin = (provider: 'google' | 'meta') => {
    window.location.href = `/customer/auth/${provider}?redirect_uri=${encodeURIComponent(window.location.origin + '/')}`;
  };

  const googleEnabled = config?.methods.google.enabled ?? true;
  const metaEnabled = config?.methods.meta.enabled ?? true;
  const hasSocial = googleEnabled || metaEnabled;
  const signupFields = config?.signupFields ?? [];
  const consentCheckboxes = config?.consentCheckboxes ?? [];

  return (
    <AuthLayout title="Create account" subtitle="Get started — it's free">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        {/* Dynamic signup fields */}
        {signupFields.length > 0 && (
          <div className="space-y-4">
            {signupFields.map((field) => (
              <DynamicField
                key={field.field}
                field={field}
                register={register}
                error={(errors[field.field]?.message as string) ?? undefined}
              />
            ))}
          </div>
        )}

        {/* Email (always present) */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-zinc-700 mb-1.5">Email address</label>
          <input id="email" type="email" autoComplete="email" className="input-field" placeholder="you@example.com" {...register('email')} />
          {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message as string}</p>}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-zinc-700 mb-1.5">Password</label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className="input-field pr-10"
              placeholder={`Minimum ${config?.passwordPolicy?.minLength ?? 8} characters`}
              {...register('password')}
            />
            <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600" tabIndex={-1}>
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <PasswordStrengthBar password={passwordValue} policy={config?.passwordPolicy} />
          {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message as string}</p>}
          {config?.passwordPolicy && <PasswordRequirementsHint policy={config.passwordPolicy} />}
        </div>

        {/* Confirm password */}
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-zinc-700 mb-1.5">Confirm password</label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              className="input-field pr-10"
              placeholder="Repeat your password"
              {...register('confirmPassword')}
            />
            <button type="button" onClick={() => setShowConfirm((v) => !v)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600" tabIndex={-1}>
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="mt-1 text-xs text-red-500">{errors.confirmPassword.message as string}</p>}
        </div>

        {/* Consent checkboxes */}
        {consentCheckboxes.length > 0 && (
          <div className="space-y-3 pt-1">
            {consentCheckboxes.map((consent) => (
              <Controller
                key={consent.id}
                name={`consent_${consent.id}`}
                control={control}
                defaultValue={false}
                render={({ field: { onChange, value, ref } }) => (
                  <div>
                    <div className="flex items-start gap-2.5">
                      <input
                        ref={ref}
                        id={`consent_${consent.id}`}
                        type="checkbox"
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                        className="h-4 w-4 mt-0.5 rounded border-zinc-300 text-[--color-primary] focus:ring-[--color-primary] cursor-pointer"
                      />
                      <label
                        htmlFor={`consent_${consent.id}`}
                        className="text-sm text-zinc-600 cursor-pointer select-none leading-snug"
                      >
                        {consent.label}
                        {consent.linkUrl && consent.linkText && (
                          <>
                            {' '}
                            <a
                              href={consent.linkUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[--color-primary] hover:opacity-80 underline"
                            >
                              {consent.linkText}
                            </a>
                          </>
                        )}
                        {consent.required && <span className="text-red-400 ml-0.5">*</span>}
                      </label>
                    </div>
                    {errors[`consent_${consent.id}`] && (
                      <p className="mt-1 ml-6 text-xs text-red-500">
                        {errors[`consent_${consent.id}`]?.message as string}
                      </p>
                    )}
                  </div>
                )}
              />
            ))}
          </div>
        )}

        {apiError && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
            <p className="text-sm text-red-600">{apiError}</p>
          </div>
        )}

        <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
          {isSubmitting ? <><Loader2 size={16} className="animate-spin" /> Creating account…</> : 'Create account'}
        </button>

        {/* Social divider */}
        {hasSocial && (
          <>
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-200" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-zinc-400">or sign up with</span></div>
            </div>

            <div className={`grid gap-3 ${googleEnabled && metaEnabled ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {googleEnabled && (
                <button type="button" onClick={() => handleSocialLogin('google')} className="social-btn">
                  <GoogleIcon /> Google
                </button>
              )}
              {metaEnabled && (
                <button type="button" onClick={() => handleSocialLogin('meta')} className="social-btn">
                  <MetaIcon /> Meta
                </button>
              )}
            </div>
          </>
        )}

        <p className="text-center text-sm text-zinc-500">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-[--color-primary] hover:opacity-80">Sign in</Link>
        </p>
      </form>
    </AuthLayout>
  );
}

// ─── Dynamic field renderer ──────────────────────────────────────────────────

function DynamicField({
  field,
  register,
  error,
}: {
  field: NonNullable<PublicAuthConfig['signupFields']>[number];
  register: ReturnType<typeof useForm>['register'];
  error?: string;
}) {
  const inputType =
    field.type === 'email' ? 'email' :
    field.type === 'date' ? 'date' :
    field.type === 'phone' ? 'tel' :
    'text';

  return (
    <div>
      <label htmlFor={field.field} className="block text-sm font-medium text-zinc-700 mb-1.5">
        {field.label}
        {field.required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {field.type === 'select' && field.options ? (
        <select
          id={field.field}
          className="input-field"
          {...register(field.field)}
        >
          <option value="">{field.placeholder ?? `Select ${field.label.toLowerCase()}`}</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      ) : (
        <input
          id={field.field}
          type={inputType}
          autoComplete={
            field.field === 'firstName' ? 'given-name' :
            field.field === 'lastName' ? 'family-name' :
            field.field === 'phone' ? 'tel' :
            undefined
          }
          className="input-field"
          placeholder={field.placeholder ?? ''}
          {...register(field.field)}
        />
      )}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

// ─── Password requirements hint ──────────────────────────────────────────────

function PasswordRequirementsHint({ policy }: { policy: PublicAuthConfig['passwordPolicy'] }) {
  const hints: string[] = [];
  if (policy.requireUppercase) hints.push('uppercase letter');
  if (policy.requireDigit) hints.push('digit');
  if (policy.requireSpecialChar) hints.push('special character');
  if (hints.length === 0) return null;

  return (
    <p className="mt-1 text-xs text-zinc-400">
      Must include: {hints.join(', ')}
    </p>
  );
}

// ─── Social icons ─────────────────────────────────────────────────────────────

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function MetaIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}
